// /src/pages/DashboardsAdminPage.js
//
// One surface for configuring every dashboard: pick a dashboard, toggle which
// cards are shown, and set each time-capable card's period. Admin only; the
// API enforces that independently.
//
// Layout (drag) is a later step -- this delivers enable/disable and the
// time-range parameter, which is where the value is.
import React, { useEffect, useState } from "react";

function api(path, opts = {}) {
  const token = localStorage.getItem("token");
  return fetch(`/api${path}`, {
    method: opts.method || "GET",
    headers: {
      ...(opts.body ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  }).then(async (res) => {
    const text = await res.text();
    let body = null;
    try { body = JSON.parse(text); } catch { /* keep raw */ }
    if (!res.ok) throw new Error(body?.detail || body?.error || `${res.status}`);
    return body;
  });
}

export default function DashboardsAdminPage() {
  const [dashboards, setDashboards] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [selected, setSelected] = useState("");
  const [selectedPath, setSelectedPath] = useState("");
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    api("/dashboards/admin/dashboards")
      .then((d) => {
        setDashboards(d.dashboards || []);
        setPeriods(d.periods || []);
        if (d.dashboards?.length) {
          setSelected(d.dashboards[0].key);
          setSelectedPath(d.dashboards[0].path);
        }
      })
      .catch((e) => setErr(e.message));
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoading(true); setMsg(""); setErr("");
    api(`/dashboards/admin/config/${selected}`)
      .then((d) => setCards(d.cards || []))
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [selected]);

  const setCard = (i, patch) =>
    setCards((cs) => cs.map((c, j) => (j === i ? { ...c, ...patch } : c)));

  const save = () => {
    setSaving(true); setMsg(""); setErr("");
    api(`/dashboards/admin/config/${selected}`, {
      method: "PUT",
      body: {
        cards: cards.map((c) => ({
          source_key: c.source_key,
          metric_key: c.metric_key,
          enabled: c.enabled,
          params: { period: c.supports_period ? c.period : "all" },
          sort_order: c.sort_order,
        })),
      },
    })
      .then((r) => setMsg(`Saved ${r.saved} cards.`))
      .catch((e) => setErr(e.message))
      .finally(() => setSaving(false));
  };

  // Group by source so a mixed dashboard reads clearly.
  const bySource = {};
  cards.forEach((c, i) => {
    (bySource[c.product] = bySource[c.product] || []).push({ ...c, i });
  });

  return (
    <div className="dash-page">
      <div className="dash-page-head">
        <h2>Dashboards Admin</h2>
        <div className="dash-head-actions">
          <a className="btn" href={`${selectedPath}?edit=1`}
             onClick={(e) => { if (!selectedPath) e.preventDefault(); }}>
            Edit Layout
          </a>
          <button className="btn" onClick={save} disabled={saving || !cards.length}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="dash-admin-controls">
        <label>
          Dashboard&nbsp;
          <select className="form-control" value={selected}
                  onChange={(e) => {
                    setSelected(e.target.value);
                    const d = dashboards.find((x) => x.key === e.target.value);
                    setSelectedPath(d ? d.path : "");
                  }}
                  style={{ display: "inline-block", width: "auto" }}>
            {dashboards.map((d) => (
              <option key={d.key} value={d.key}>{d.name}</option>
            ))}
          </select>
        </label>
        {msg && <span className="dash-admin-msg">{msg}</span>}
      </div>

      {err && <div className="alert-danger">{err}</div>}
      {loading && <div className="dash-msg">Loading…</div>}

      {!loading && Object.entries(bySource).map(([product, list]) => (
        <section key={product} className="dash-admin-source">
          <h3>{product}</h3>
          <table className="table dash-admin-table">
            <thead>
              <tr>
                <th style={{ width: "70px" }}>Shown</th>
                <th>Card</th>
                <th>Domain</th>
                <th style={{ width: "180px" }}>Time Range</th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={`${c.source_key}-${c.metric_key}`}
                    className={c.enabled ? "" : "dash-admin-off"}>
                  <td>
                    <input type="checkbox" checked={c.enabled}
                           onChange={(e) => setCard(c.i, { enabled: e.target.checked })} />
                  </td>
                  <td>{c.title}</td>
                  <td><span className="badge">{c.domain}</span></td>
                  <td>
                    {c.supports_period ? (
                      <select className="form-control" value={c.period}
                              disabled={!c.enabled}
                              onChange={(e) => setCard(c.i, { period: e.target.value })}
                              style={{ padding: "2px 6px" }}>
                        {periods.map(([k, label]) => (
                          <option key={k} value={k}>{label}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="dash-muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </div>
  );
}
